import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { portfolioApi } from '../services/api';
import {
  MapPin, Clock, Mail, Linkedin, Twitter, Globe,
  Github, Sparkles, Eye, ExternalLink
} from 'lucide-react';

const THEME_STYLES = {
  minimal: {
    bg: 'bg-white',
    page: 'bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]',
    header: 'bg-gradient-to-br from-orange-50 to-white',
    accent: 'text-[#e8744f]',
    badge: 'bg-orange-50 text-gray-700 border border-orange-100',
    prompt: 'bg-gray-50 border border-gray-100',
    text: 'text-gray-500',
    name: 'text-gray-900',
    section: 'text-[#e8744f]',
  },
  dark: {
    bg: 'bg-gray-900',
    page: 'bg-gray-950',
    header: 'bg-gradient-to-br from-gray-800 to-gray-900',
    accent: 'text-orange-400',
    badge: 'bg-gray-700 text-gray-200 border border-gray-600',
    prompt: 'bg-gray-800 border border-gray-700',
    text: 'text-gray-400',
    name: 'text-white',
    section: 'text-orange-400',
  },
  creative: {
    bg: 'bg-orange-50',
    page: 'bg-gradient-to-br from-[#fae5dd] to-[#f5f1ed]',
    header: 'bg-gradient-to-br from-[#e8744f] to-[#d66440]',
    accent: 'text-[#e8744f]',
    badge: 'bg-orange-100 text-orange-800 border border-orange-200',
    prompt: 'bg-white border border-orange-100',
    text: 'text-orange-900/60',
    name: 'text-white',
    section: 'text-[#e8744f]',
  },
};

export default function PublicPortfolio() {
  const { id } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await portfolioApi.getPublic(id);
        setPortfolio(data);
      } catch (error) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e8744f] mx-auto mb-3" />
        <p className="text-sm text-gray-400 animate-pulse">Loading portfolio...</p>
      </div>
    </div>
  );

  if (notFound || !portfolio) return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f1ed] to-[#fae5dd] flex flex-col items-center justify-center p-6 text-center">
      <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
        <Sparkles className="text-[#e8744f]" size={28} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Portfolio not found</h2>
      <p className="text-sm text-gray-500 mb-6">This portfolio may be private or no longer exists.</p>
      <Link to="/register">
        <button className="px-6 py-3 rounded-full bg-[#e8744f] hover:bg-[#d66440] text-white text-sm font-semibold shadow-md transition">
          Build Your Own Portfolio →
        </button>
      </Link>
    </div>
  );

  const ts = THEME_STYLES[portfolio.theme] || THEME_STYLES.minimal;
  const social = portfolio.social_links || {};
  const prompts = [
    { icon: '🌟', q: 'What makes me different', val: portfolio.uniqueness },
    { icon: '🔥', q: 'What excites me', val: portfolio.passion },
    { icon: '🏆', q: "I'm most proud of", val: portfolio.proudest_work },
    { icon: '💭', q: 'Dream project', val: portfolio.dream_project },
    { icon: '☀️', q: 'A typical day', val: portfolio.typical_day },
    { icon: '💬', q: 'Best career advice', val: portfolio.career_advice },
  ].filter(p => p.val);

  return (
    <div className={`min-h-screen ${ts.page}`}>

      {/* Minimal top bar — just branding, no nav */}
      <div className="border-b border-orange-100/60 bg-white/60 backdrop-blur-sm px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-[#e8744f] to-[#d66440] shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">Folio</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Portfolio matching platform</p>
              </div>
        </Link>
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <Eye size={12} /> Public profile
        </span>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* ── Portfolio Card ── */}
        <div className={`rounded-3xl overflow-hidden shadow-lg border border-gray-100 ${ts.bg}`}>

          {/* Header */}
          <div className={`p-6 pb-5 ${ts.header}`}>
            <div className="flex items-start gap-4">
              <div className={`w-20 h-20 rounded-full overflow-hidden flex-shrink-0 border-2 ${portfolio.theme === 'creative' ? 'border-white/30' : 'border-orange-200'}`}>
                {portfolio.avatar_url ? (
                  <img src={portfolio.avatar_url} alt={portfolio.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#e8744f] flex items-center justify-center text-white text-3xl font-bold">
                    {portfolio.name?.charAt(0)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className={`text-2xl font-bold truncate ${ts.name}`}>{portfolio.name}</h1>
                <p className={`text-sm mb-2 ${portfolio.theme === 'creative' ? 'text-white/80' : ts.text}`}>{portfolio.title}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(portfolio.profession || portfolio.branch) && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                      🎓 {[portfolio.branch, portfolio.profession].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {portfolio.availability && (
                    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                      portfolio.availability === 'Actively looking' ? 'bg-green-500/20 text-green-600' :
                      portfolio.availability === 'Not available' ? 'bg-red-500/20 text-red-500' :
                      'bg-orange-500/20 text-orange-600'
                    }`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />{portfolio.availability}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  {portfolio.location && (
                    <span className={`flex items-center gap-1 ${portfolio.theme === 'creative' ? 'text-white/70' : ts.text}`}>
                      <MapPin size={10} />{portfolio.location}
                    </span>
                  )}
                  {portfolio.years_experience && (
                    <span className={`flex items-center gap-1 ${portfolio.theme === 'creative' ? 'text-white/70' : ts.text}`}>
                      <Clock size={10} />{portfolio.years_experience}+ yrs
                    </span>
                  )}
                </div>
              </div>
            </div>
            {portfolio.tagline && (
              <p className={`mt-4 text-sm italic ${portfolio.theme === 'creative' ? 'text-white/80' : ts.text}`}>
                "{portfolio.tagline}"
              </p>
            )}
          </div>

          {/* Gallery */}
          {portfolio.gallery?.length > 0 && (
            <div className="px-5 py-3 flex gap-2 overflow-x-auto">
              {portfolio.gallery.map((url, i) => (
                <div key={i} className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={url} alt="gallery" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* Body */}
          <div className="p-5 space-y-5">

            {portfolio.bio && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>About Me</h3>
                <p className={`text-sm leading-relaxed ${ts.text}`}>{portfolio.bio}</p>
              </div>
            )}

            {portfolio.skills?.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {portfolio.skills.map((s, i) => <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{s}</span>)}
                </div>
              </div>
            )}

            {portfolio.tools?.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>Tools</h3>
                <div className="flex flex-wrap gap-2">
                  {portfolio.tools.map((t, i) => <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{t}</span>)}
                </div>
              </div>
            )}

            {portfolio.languages?.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {portfolio.languages.map((l, i) => <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{l}</span>)}
                </div>
              </div>
            )}

            {portfolio.projects?.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-3 ${ts.section}`}>Projects</h3>
                <div className="grid grid-cols-2 gap-3">
                  {portfolio.projects.map((proj, i) => (
                    <div key={i} className={`rounded-2xl overflow-hidden ${ts.prompt}`}>
                      {proj.image_url && <img src={proj.image_url} alt={proj.title} className="w-full h-28 object-cover" />}
                      <div className="p-3">
                        <p className={`text-sm font-bold ${portfolio.theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{proj.title}</p>
                        {proj.description && <p className={`text-xs mt-0.5 line-clamp-2 ${ts.text}`}>{proj.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {prompts.length > 0 && (
              <div>
                <h3 className={`font-bold text-sm mb-3 ${ts.section}`}>Get to Know Me</h3>
                <div className="space-y-3">
                  {prompts.map((pr, i) => (
                    <div key={i} className={`rounded-2xl p-4 ${ts.prompt}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-base">{pr.icon}</span>
                        <span className={`text-xs font-bold ${ts.accent}`}>{pr.q}</span>
                      </div>
                      <p className={`text-sm leading-relaxed ${ts.text}`}>{pr.val}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(social.linkedin || social.github || social.twitter || social.portfolio_url) && (
              <div>
                <h3 className={`font-bold text-sm mb-2 ${ts.section}`}>Connect</h3>
                <div className="flex flex-wrap gap-2">
                  {social.linkedin && (
                    <a href={social.linkedin.startsWith('http') ? social.linkedin : `https://${social.linkedin}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium hover:scale-105 transition ${ts.badge}`}>
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  )}
                  {social.github && (
                    <a href={social.github.startsWith('http') ? social.github : `https://${social.github}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium hover:scale-105 transition ${ts.badge}`}>
                      <Github size={12} /> GitHub
                    </a>
                  )}
                  {social.twitter && (
                    <a href={social.twitter.startsWith('@') ? `https://twitter.com/${social.twitter.slice(1)}` : `https://twitter.com/${social.twitter}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium hover:scale-105 transition ${ts.badge}`}>
                      <Twitter size={12} /> Twitter
                    </a>
                  )}
                  {social.portfolio_url && (
                    <a href={social.portfolio_url.startsWith('http') ? social.portfolio_url : `https://${social.portfolio_url}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium hover:scale-105 transition ${ts.badge}`}>
                      <Globe size={12} /> Website
                    </a>
                  )}
                </div>
              </div>
            )}

            {portfolio.contact_email && (
              <div className={`flex items-center gap-2 rounded-xl p-3 ${ts.prompt}`}>
                <Mail size={14} className={ts.accent} />
                <span className={`text-sm ${ts.text}`}>{portfolio.contact_email}</span>
              </div>
            )}

          </div>
        </div>

        {/* ── Build Your Own CTA ── */}
        <div className="rounded-3xl border border-orange-100 bg-white/80 backdrop-blur-sm p-6 text-center space-y-3 shadow-sm">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#e8744f] to-[#d66440] shadow-md mx-auto">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <h3 className="font-bold text-gray-900 text-base">Like what you see?</h3>
          <p className="text-sm text-gray-500 max-w-xs mx-auto">
            Build your own portfolio in minutes — answer a few prompts and get a shareable profile like this one.
          </p>
          <Link to="/register">
            <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#e8744f] hover:bg-[#d66440] text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200 mt-1">
              Build Your Own Portfolio
              <ExternalLink size={14} />
            </button>
          </Link>
          <p className="text-xs text-gray-400">Free · No design skills needed</p>
        </div>

      </div>
    </div>
  );
}